/**
 * Client-side CSV export helper.
 *
 * Used by Property Search + Sold Comps to hand reps a ready-to-import
 * spreadsheet for cold-mail / cold-call campaigns. No server round-trip
 * — the data is already in the browser, so we serialise + trigger a
 * download in one shot. Escapes quotes / newlines / commas per RFC 4180
 * so Excel + Google Sheets open cleanly on every column.
 */

/** Escape a single cell value per RFC 4180. */
function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  // Wrap in quotes if the cell contains any of: comma, quote, newline.
  // Inner quotes get doubled up ("Sam ""Sammy"" Jones").
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Serialise `rows` (array of objects) into a CSV string.
 *
 * @param {Array<object>} rows  Array of plain-object rows.
 * @param {Array<{key:string,label:string,format?:(v:any,row:object)=>any}>} columns
 *   Column spec — controls order and lets caller reshape values
 *   (formatting money, joining booleans into "yes/no", etc.).
 */
export function toCsv(rows, columns) {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const header = columns.map((c) => csvCell(c.label)).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => csvCell(c.format ? c.format(r[c.key], r) : r[c.key]))
        .join(","),
    )
    .join("\n");
  return `${header}\n${body}\n`;
}

/**
 * Trigger a browser download of `csv` as `filename`.csv. Works in every
 * modern browser without a shim — we build a Blob and click a temporary
 * anchor tag, cleaning up after so the DOM doesn't accumulate ghosts.
 */
export function downloadCsv(csv, filename = "flippilot-export.csv") {
  if (!csv) return;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Combined helper — pass rows + columns, get download. */
export function exportCsv(rows, columns, filename) {
  const csv = toCsv(rows, columns);
  downloadCsv(csv, filename);
}
